import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link } from '@tanstack/react-router';
import { Route } from '@src/routes/support/support-faq';
import { GridContainer, Grid, Breadcrumb, BreadcrumbBar, Button, Tag } from '@trussworks/react-uswds';
import Icon from '@mdi/react';
import {
  mdiAccountCircle,
  mdiPlayCircle,
  mdiMap,
  mdiClipboardText,
  mdiPencilBox,
  mdiFileUpload,
  mdiWrench,
  mdiMagnify,
} from '@mdi/js';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { useModal } from '@stores';
import VideoModal from './modals/VideoModal';
import faqData from './data/faqData.json';
import './support.scss';

import Accordion from '@components/accordion/Accordion';

// Icon mapping for categories
const iconMap = {
  mdiAccountCircle,
  mdiPlayCircle,
  mdiMap,
  mdiClipboardText,
  mdiPencilBox,
  mdiFileUpload,
  mdiWrench,
};

const SupportFAQ = () => {
  const navigate = useNavigate();
  const { search = '' } = Route.useSearch();
  const { openModal } = useModal();
  // const { openTicketModal } = useSupportWidget();
  const [searchQuery, setSearchQuery] = useState(search);
  const [activeCategory, setActiveCategory] = useState(null);

  // Helper to check if URL is a video link
  const isVideoUrl = (url) => {
    if (!url) return false;
    return url.includes('youtube.com') || url.includes('youtu.be') || url.includes('dvidshub.net/video');
  };

  // Custom link renderer for ReactMarkdown - opens videos in modal
  const MarkdownLink = ({ href, children }) => {
    if (isVideoUrl(href)) {
      return (
        <button
          type='button'
          className='faq-video-link'
          onClick={() => openModal(VideoModal, { title: children, videoUrl: href })}
        >
          {children}
        </button>
      );
    }
    return <a href={href}>{children}</a>;
  };

  // Get hash from URL for category navigation
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      setActiveCategory(hash);
      // Scroll to category after a brief delay
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  // Filter FAQs based on search
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) {
      return faqData.categories;
    }

    const query = searchQuery.toLowerCase();
    return faqData.categories
      .map((category) => ({
        ...category,
        items: category.items.filter(
          (item) =>
            item.question.toLowerCase().includes(query) ||
            item.answer.toLowerCase().includes(query) ||
            item.tags.some((tag) => tag.toLowerCase().includes(query))
        ),
      }))
      .filter((category) => category.items.length > 0);
  }, [searchQuery]);

  // Count total results
  const totalResults = useMemo(() => {
    return filteredCategories.reduce((acc, cat) => acc + cat.items.length, 0);
  }, [filteredCategories]);

  const handleCategoryClick = useCallback((categoryId) => {
    setActiveCategory(categoryId);
    window.location.hash = categoryId;
    const element = document.getElementById(categoryId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const value = searchQuery.trim();
    navigate({
      to: '/support/support-faq',
      search: value ? { search: value } : {},
    });
  };

  const clearSearch = () => {
    setSearchQuery('');
    navigate({ to: '/support/support-faq', search: {} });
  };

  return (
    <div className='support-faq-page'>
      <GridContainer>
        {/* Breadcrumb */}
        <BreadcrumbBar>
          <Breadcrumb>
            <Link to='/support/' className='usa-breadcrumb__link'>
              {' '}
              Support{' '}
            </Link>
          </Breadcrumb>
          <Breadcrumb current>
            <span>FAQ</span>
          </Breadcrumb>
        </BreadcrumbBar>

        <Grid row gap='lg'>
          {/* Side Navigation */}
          <Grid desktop={{ col: 3 }} tablet={{ col: 12 }} className='faq-sidebar'>
            <nav className='faq-side-nav' aria-label='FAQ categories'>
              <h3>Categories</h3>
              <ul>
                <li>
                  <button
                    className={`side-nav-link ${!activeCategory ? 'active' : ''}`}
                    onClick={() => {
                      setActiveCategory(null);
                      window.location.hash = '';
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    All Topics
                  </button>
                </li>
                {faqData.categories.map((category) => (
                  <li key={category.id}>
                    <button
                      className={`side-nav-link ${activeCategory === category.id ? 'active' : ''}`}
                      onClick={() => handleCategoryClick(category.id)}
                    >
                      <Icon path={iconMap[category.icon]} size={0.8} style={{ color: category.color }} />
                      <span>{category.title}</span>
                      <span className='item-count'>{category.items.length}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Need More Help */}
            <div className='sidebar-section need-help'>
              <h3>Need More Help?</h3>
              <p>Can't find what you're looking for?</p>
              {/* <Button type='button' onClick={openTicketModal}>
                Contact Support
              </Button> */}
            </div>
          </Grid>

          {/* Main Content */}
          <Grid desktop={{ col: 9 }} tablet={{ col: 12 }} className='faq-main-content'>
            {/* Search */}
            <div className='faq-search-section'>
              <form onSubmit={handleSearch} className='faq-search-form'>
                <div className='faq-search-wrapper'>
                  <Icon path={mdiMagnify} size={1} className='search-icon' />
                  <input
                    type='text'
                    className='faq-search-input'
                    placeholder='Search FAQs...'
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label='Search FAQs'
                  />
                  {searchQuery && (
                    <button type='button' className='clear-search' onClick={clearSearch}>
                      Clear
                    </button>
                  )}
                </div>
              </form>
              {searchQuery && (
                <p className='search-results-count'>
                  Found <strong>{totalResults}</strong> result{totalResults !== 1 ? 's' : ''} for "{searchQuery}"
                </p>
              )}
            </div>

            {/* FAQ Categories */}
            {filteredCategories.length === 0 ? (
              <div className='no-results'>
                <h3>No results found</h3>
                <p>Try adjusting your search terms or browse all categories.</p>
                <button className='clear-search-btn' onClick={clearSearch}>
                  Clear Search
                </button>
              </div>
            ) : (
              filteredCategories.map((category) => (
                <section key={category.id} id={category.id} className='faq-category-section'>
                  <div className='category-header'>
                    <div className='category-icon' style={{ backgroundColor: category.color }}>
                      <Icon path={iconMap[category.icon]} size={1} color='#fff' />
                    </div>
                    <div>
                      <h2>{category.title}</h2>
                      <p className='category-description'>{category.description}</p>
                    </div>
                  </div>

                  <div className='accordion-list'>
                    {category.items.map((item) => (
                      <Accordion key={item.id} id={item.id} title={item.question}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: MarkdownLink }}>
                          {item.answer}
                        </ReactMarkdown>
                        {item.tags && item.tags.length > 0 && (
                          <div className='faq-tags'>
                            {item.tags.map((tag) => (
                              <Tag key={tag} className='faq-tag'>
                                {tag}
                              </Tag>
                            ))}
                          </div>
                        )}
                        {item.relatedArticle && (
                          <div className='faq-related'>
                            <Button
                              type='button'
                              onClick={() => navigate({ to: item.relatedArticle })}
                              className='related-guide-link'
                            >
                              View full guide
                            </Button>
                          </div>
                        )}
                      </Accordion>
                    ))}
                  </div>
                </section>
              ))
            )}
          </Grid>
        </Grid>
      </GridContainer>
    </div>
  );
};

export default SupportFAQ;
